import {createContext, useEffect, useState} from 'react';
import type {FunctionComponent} from 'react';
import {RoutingApi, type ApiTokenAddress} from '@swap-coffee/sdk';
import {useTonConnectUI} from '@tonconnect/ui-react';
import {LinearProgress, Typography} from '@mui/material';
import styles from '@/pages/styles.module.css';
import FlexContainer from '@/components/common/flex-container';
import Button from '@/components/common/button';
import SwapWidgetNew from '@/components/swap-widget-new';
import type TokenMetadataItem from '@/components/tokens/types/token-metadata-item.ts';
import {useDebounce} from 'use-debounce';
import round from 'lodash.round';
import {usePreciseDecimalInput} from '@/hooks/use-precise-decimal-input.ts';
import SlippageSettingsDialog from '@/components/swap-widget-new/slippage-setting-dialog';
import type {SwapWidgetContextProps} from '@/pages/swap-widget/types/swap-widget-context-props.ts';
import stylesSwapWidget from './styles.module.css';
import {styledMod} from '@/utils/styled-mod.tsx';

const LinkSwapCoffee = styledMod('a', stylesSwapWidget['link-swap-coffee']);
const SwapWidgetContainer = styledMod('div', styles['page-container']);

const DEFAULT_DEBOUNCE_DELAY = 1000;
const MIN_AMOUNT_VALUE = 0.000001;
const MIN_SLIPPAGE_VALUE = 0.01;
const NATIVE_BLOCKCHAIN_SYMBOL = 'ton';
const DECIMALS_SEND_AMOUNT_VALUE = 6;
const DECIMALS_RECEIVE_AMOUNT_VALUE = 8;
const DECIMALS_SLIPPAGE_VALUE = 2;

const routingApi = new RoutingApi();

export type SwapWidgetProps = {
  tokensMetadata: TokenMetadataItem[];
};

export const SwapWidgetContext = createContext<SwapWidgetContextProps>({
  isSendAmountTmp: false,
  sendAmountValue: null,
  sendTokenImageSrc: null,
  sendTokenName: null,
  receiveTokenImageSrc: null,
  sendTokenAddress: null,
  sendUsdAmount: null,
  receiveAmountValue: null,
  receiveTokenName: null,
  receiveTokenAddress: null,
  receiveUsdAmount: null,
  slippageValue: null,
});

const SwapWidget: FunctionComponent<SwapWidgetProps> = (props: SwapWidgetProps) => {
  const [tonConnectUi] = useTonConnectUI();
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [isReceiveData, setIsReceiveData] = useState(false);
  const [sendTokenAddress, setSendTokenAddress] = useState<string | null>(null);
  const [receiveTokenAddress, setReceiveTokenAddress] = useState<string | null>(null);
  const [sendTokenName, setSendTokenName] = useState<string | null>(null);
  const [receiveTokenName, setReceiveTokenName] = useState<string | null>(null);
  const [receiveAmountValue, setReceiveAmountValue] = useState<string | null>('0');
  const [sendUsdAmount, setSendUsdAmount] = useState<number | null>(null);
  const [receiveUsdAmount, setReceivedUsdAmount] = useState<number | null>(null);
  const [isVisibleSlippageSettingDialog, setIsVisibleSlippageSettingDialog] = useState<boolean>(false);
  const [sendTokenImageSrc, setSendTokenImageSrc] = useState<string | null>(null);
  const [receiveTokenImageSrc, setReceiveTokenImageSrc] = useState<string | null>(null);

  const {
    onChange: onSplippageValueChange,
    value: slippageValue,
    setRaw: setSlippageValueRaw
  } = usePreciseDecimalInput({
    initial: 0.5,
    decimals: DECIMALS_SLIPPAGE_VALUE,
    minValue: MIN_SLIPPAGE_VALUE,
  });

  const {
    onChange: onSendAmountValueChange,
    display: sendAmountValue,
    isTmpValue: isSendAmountTmp,
    setRaw: setSendAmountRaw
  } = usePreciseDecimalInput({
    initial: null,
    decimals: DECIMALS_SEND_AMOUNT_VALUE,
    minValue: MIN_AMOUNT_VALUE,
  });

  const [sendAmountValueDebounced] = useDebounce(sendAmountValue, DEFAULT_DEBOUNCE_DELAY, {maxWait: 3000});
  const [slippageValueDebounced] = useDebounce(slippageValue, DEFAULT_DEBOUNCE_DELAY);

  useEffect(() => {
    const wallet = tonConnectUi.wallet;
    if (wallet?.account?.address) {
      setWalletAddress(wallet.account.address);
    }
  }, [tonConnectUi.wallet]);

  useEffect(() => {
    if (!sendAmountValueDebounced) {
      setReceiveAmountValue('0');
      setReceivedUsdAmount(0);
      setSendUsdAmount(0);
    }

  }, [sendAmountValueDebounced]);

  useEffect(() => {
    if (sendTokenAddress && sendTokenAddress !== receiveTokenAddress && sendAmountValueDebounced !== null && Number(sendAmountValueDebounced) > MIN_AMOUNT_VALUE) {
      onTokensInformationReceive();
    }
  }, [sendTokenAddress, receiveTokenAddress, sendAmountValueDebounced]);

  useEffect(() => {
    if (props.tokensMetadata?.length > 0) {
      const sendTokenElement: TokenMetadataItem | undefined = props.tokensMetadata?.find((element: TokenMetadataItem) => element.symbol === 'TON');
      if (sendTokenElement) {
        setSendTokenAddress(sendTokenElement.symbol !== 'TON' ? sendTokenElement.address : 'native');
        setSendTokenName(sendTokenElement.symbol);
        setSendTokenImageSrc(sendTokenElement.image);
      } else {
        setSendTokenAddress(null);
        setSendTokenName(null);
        setSendTokenImageSrc(null);
      }
      const receiveTokenElement: TokenMetadataItem | undefined = props.tokensMetadata?.find((element: TokenMetadataItem) => element.symbol === 'REDOI');
      if (receiveTokenElement) {
        setReceiveTokenAddress(receiveTokenElement.address);
        setReceiveTokenName(receiveTokenElement.symbol);
        setReceiveTokenImageSrc(receiveTokenElement.image);
      } else {
        setReceiveTokenAddress(null);
        setReceiveTokenName(null);
        setReceiveTokenImageSrc(null);
      }
    }
  }, [props.tokensMetadata])

  const onVisibleSlippageSettingDialogChange = (isVisible = false) => {
    setIsVisibleSlippageSettingDialog(isVisible);
  };

  const onSendTokenAddressChange = (tokenName: string, tokenAddress: string, imgSrc: string) => {
    setSendTokenImageSrc(imgSrc);
    setSendTokenAddress(tokenAddress);
    setSendTokenName(tokenName);
    if (tokenAddress !== receiveTokenAddress) {
      if (receiveAmountValue !== null) {
        onSendAmountValueChange('0');
      }
    }
  };

  const onReceiveTokenAddressChange = (tokenName: string, tokenAddress: string, imgSrc: string) => {
    setReceiveTokenImageSrc(imgSrc);
    setReceiveTokenAddress(tokenAddress);
    setReceiveTokenName(tokenName);
    if (tokenAddress !== sendTokenAddress) {
      setReceiveAmountValue('0');
    }
  };

  const onReceiveAmountValueChange = (valueAmount: string) => {
    setReceiveAmountValue(valueAmount);
  };

  const onSwapParametersChange = () => {

    if (receiveAmountValue !== null) {
      setSendAmountRaw(receiveAmountValue?.toString());
    }
    setSendTokenAddress(receiveTokenAddress);
    setSendTokenName(receiveTokenName);
    setSendTokenImageSrc(receiveTokenImageSrc);
    setReceiveAmountValue(sendAmountValueDebounced);
    setReceiveTokenAddress(sendTokenAddress);
    setReceiveTokenName(sendTokenName);
    setReceiveTokenImageSrc(sendTokenImageSrc);
  };

  const onTokensInformationReceive = async () => {

    if (isSendAmountTmp) {
      return;
    }

    setIsReceiveData(true);

    if (sendTokenAddress && receiveTokenAddress && sendAmountValueDebounced !== null && Number(sendAmountValueDebounced) > 0.000001) {

      const assetIn: ApiTokenAddress = {blockchain: NATIVE_BLOCKCHAIN_SYMBOL, address: sendTokenAddress};
      const assetOut: ApiTokenAddress = {
        blockchain: NATIVE_BLOCKCHAIN_SYMBOL,
        address: receiveTokenAddress,
      };

      if (!walletAddress) {
        console.log('First, connect your wallet');
        return;
      }

      try {
        const route = await routingApi.buildRoute({
          input_token: assetIn,
          output_token: assetOut,
          input_amount: Number(sendAmountValueDebounced),
        });

        setSendUsdAmount(route.data.output_usd);
        setReceivedUsdAmount(route.data.input_usd);
        setReceiveAmountValue(round(route?.data?.output_amount, DECIMALS_RECEIVE_AMOUNT_VALUE).toString());

      } catch (error) {
        console.error('Swap error:', error?.toString());
      } finally {
        setIsReceiveData(false);
      }
    }
  };


  const onTokensSwap = async () => {
    if (isSendAmountTmp) {
      return;
    }

    if (sendTokenAddress && receiveTokenAddress && sendAmountValue !== null && slippageValue !== null) {

      const assetIn: ApiTokenAddress = {blockchain: NATIVE_BLOCKCHAIN_SYMBOL, address: sendTokenAddress}; // TON
      const assetOut: ApiTokenAddress = {
        blockchain: NATIVE_BLOCKCHAIN_SYMBOL,
        address: receiveTokenAddress,
      };

      if (!walletAddress) {
        alert('First, connect your wallet');
        return;
      }

      setIsSwapping(true);

      try {
        const route = await routingApi.buildRoute({
          input_token: assetIn,
          output_token: assetOut,
          input_amount: Number(sendAmountValue), // 1 jetton в формате nano (в зависимости от decimals)
        });

        const txs = await routingApi.buildTransactionsV2({
          sender_address: walletAddress,
          slippage: round(slippageValue / 100, 6),
          paths: route.data.paths,
        });

        await tonConnectUi.sendTransaction({
          validUntil: Math.floor(Date.now() / 1000) + 300,
          messages: txs.data.transactions.map(tx => ({
            address: tx.address,
            amount: tx.value,
            payload: tx.cell,
          })),
        });

      } catch (error) {
        console.error('Swap error:', error?.toString());
      } finally {
        setIsSwapping(false);
      }
    }
  };

  const isLoading: boolean = isSwapping || props.tokensMetadata.length === 0;


  const swapWidgetContextValue: SwapWidgetContextProps = {
    isSendAmountTmp,
    sendAmountValue,
    sendTokenImageSrc,
    sendTokenName,
    sendUsdAmount,
    receiveTokenImageSrc,
    sendTokenAddress,
    receiveAmountValue,
    receiveTokenName,
    receiveTokenAddress,
    receiveUsdAmount,
    slippageValue
  }

  return (
    <SwapWidgetContext.Provider value={swapWidgetContextValue}>
      <SlippageSettingsDialog open={isVisibleSlippageSettingDialog}
                              onSlippageChange={onSplippageValueChange}
                              onClose={onVisibleSlippageSettingDialogChange}
                              setSlippageValueRaw={setSlippageValueRaw}
                              slippage={slippageValueDebounced}
      />
      {isLoading && <LinearProgress/>}
      <SwapWidgetContainer>
        <Typography variant="h2" className={styles['page-title']}>
          Swap widget
        </Typography>
        <FlexContainer width="initial" overflowx="auto" margin="15px 0 10px 0" justifycontent="center">
          <FlexContainer flexdirection="column" width="initial">
            <SwapWidgetNew
              tokensMetadata={props.tokensMetadata}
              onSendTokenAddressChange={onSendTokenAddressChange}
              onReceiveTokenAddressChange={onReceiveTokenAddressChange}
              onSendAmountValueChange={onSendAmountValueChange}
              onReceiveAmountValueChange={onReceiveAmountValueChange}
              onSwapParametersChange={onSwapParametersChange}
              onVisibleSlippageSettingDialogChange={onVisibleSlippageSettingDialogChange}
              isVisibleSlippageSettingDialog={isVisibleSlippageSettingDialog}
            />
            <LinkSwapCoffee>Swap через Swap.Coffee</LinkSwapCoffee>
            <FlexContainer margin="5px 0 0 0">
              <Button width="100%" variant="contained" type="button" onClick={onTokensSwap}
                      disabled={isSwapping || !walletAddress || !receiveTokenAddress || !sendTokenAddress || !sendAmountValue || sendTokenAddress === receiveTokenAddress || Number(sendAmountValue) < MIN_AMOUNT_VALUE || Number(receiveAmountValue) < MIN_AMOUNT_VALUE}>
                {isSwapping ? 'SWAPPING...' : !isReceiveData ? 'SWAP' : ''}
                {isReceiveData ? 'RECEIVE DATA' : ''}
              </Button>
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
      </SwapWidgetContainer>
    </SwapWidgetContext.Provider>
  );
};

export default SwapWidget;
